export const DASHBOARD_MODEL_SCHEMA_VERSION = 2;

export type DashboardFilterOption = {
  value: string;
  label: string;
};

export type DashboardFilterType = 'data' | 'lista' | 'texto' | 'numero';

type DashboardFilterLookup = {
  sql: string;
  campoValor: string;
  campoRotulo: string;
};

/** O que o filtro de data representa no período do painel. */
export type DashboardFilterRole = 'inicio' | 'fim';

/**
 * Grupo dos filtros que formam um período. Hoje o Designer monta um período por
 * painel, e o servidor casa o começo com o fim pelo grupo — declarar o mesmo
 * valor nos dois é o que faz o par existir.
 */
export const GRUPO_DE_PERIODO = 'periodo';

export type DashboardFilterDef = {
  id: string;
  label: string;
  tipo?: DashboardFilterType;
  multiplo?: boolean;
  visivel?: boolean;
  options?: DashboardFilterOption[];
  valorPadrao: string;
  lookup?: DashboardFilterLookup;
  papel?: DashboardFilterRole;
  grupo?: string;
};

export function formatarData(data: Date): string {
  const mes = `${data.getMonth() + 1}`.padStart(2, '0');
  const dia = `${data.getDate()}`.padStart(2, '0');

  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** Menor data que o SQL Server aceita em datetime; abaixo disso ele estoura. */
export const DATA_MINIMA_SQL = '1753-01-01';

/** Maior data que o SQL Server aceita em datetime. */
export const DATA_MAXIMA_SQL = '9999-12-31';

/**
 * Diz se o valor de um filtro de data já pode ir para o servidor.
 *
 * O campo nativo de data emite uma mudança a cada tecla do ano, então quem
 * digita 2026 passa antes por 0002, 0020 e 0202. Mandar isso para o banco
 * derruba a consulta com "SqlDateTime overflow", e o usuário vê uma fila de
 * erros enquanto ainda está digitando. Vazio continua valendo: é o filtro
 * limpo.
 */
export function dataAplicavel(valor: string): boolean {
  if (!valor.trim()) return true;

  if (!/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false;

  return valor >= DATA_MINIMA_SQL && valor <= DATA_MAXIMA_SQL;
}

export function filtrosAplicaveis(
  filtros: DashboardFilterDef[],
  valores: Record<string, string>
): boolean {
  return filtros
    .filter((filtro) => filtro.tipo === 'data')
    .every((filtro) => dataAplicavel(valores[filtro.id] ?? ''));
}

export function resolverValorPadrao(filtro: DashboardFilterDef, hoje = new Date()): string {
  if (filtro.tipo !== 'data') return filtro.valorPadrao;

  if (filtro.valorPadrao === 'hoje') return formatarData(hoje);

  const relativo = /^([+-]?\d+)d$/i.exec(filtro.valorPadrao);

  if (relativo) {
    const deslocado = new Date(hoje);
    deslocado.setDate(deslocado.getDate() + Number(relativo[1]));
    return formatarData(deslocado);
  }

  return filtro.valorPadrao;
}

/** "2026-07-01" e "2026-07-31" viram "01/07 – 31/07". */
export function formatarIntervaloCurto(inicio: string, fim: string): string {
  const curto = (valor: string) => valor.split('-').slice(1).reverse().join('/');

  return `${curto(inicio)} – ${curto(fim)}`;
}

export type DashboardWidgetLayout = {
  coluna: number;
  linha: number;
  largura: number;
  altura: number;
};

export type DashboardWidgetFormat = 'inteiro' | 'moeda' | 'percentual';

export type DashboardColumnFormat = 'texto' | 'decimal' | 'moeda' | 'percentual';

export type DashboardColumnDef = {
  campo: string;
  titulo: string;
  formato?: DashboardColumnFormat;
};

export type DashboardSortDef = {
  campo: string;
  descendente?: boolean;
};

export type DashboardPivotMeasureDef = {
  campo: string;
  titulo: string;
  formato?: DashboardColumnFormat;
  agregacao?: 'contagem' | 'soma' | 'media' | 'minimo' | 'maximo';
};

export type DashboardPivotDef = {
  linhas: DashboardColumnDef[];
  colunas: DashboardColumnDef[];
  medidas: DashboardPivotMeasureDef[];
};

type DashboardChartSeriesDef = {
  campo: string;
  titulo: string;
  agregacao?: string;
  formato?: DashboardColumnFormat;
  /** Ausente, a série é desenhada com a marca do widget. */
  tipo?: 'barras' | 'linha';
};

type DashboardChartFilterDef = {
  campo: string;
  valor: string;
};

export type DashboardChartDef = {
  argumento: string;
  agrupamentoData?: string;
  topN?: number;
  serieDimensao?: string;
  serieDimensaoAgrupamento?: string;
  filtros?: DashboardChartFilterDef[];
  series: DashboardChartSeriesDef[];
};

export type DashboardGaugeMeasureDef = {
  titulo: string;
  campoValor: string;
  agregacaoValor?: string;
  campoAlvo?: string;
  agregacaoAlvo?: string;
  formato?: DashboardColumnFormat;
};

export type DashboardGaugeDef = {
  medidores: DashboardGaugeMeasureDef[];
};

/**
 * As cores dos dados de um widget, escolhidas por quem monta o dashboard.
 *
 * Ausente, o widget usa a paleta do tema — que é o que todo dashboard fez até
 * esta versão. A precedência ao desenhar é sempre a mesma: `porNome` vence
 * `personalizadas`, que vence `paleta`, que vence o tema.
 */
export type DashboardWidgetColorsDef = {
  /** Id de uma paleta do design system, que tem variante para cada tema. */
  paleta?: string;
  /** Hex livres, na ordem das séries/categorias. Não se adaptam ao tema. */
  personalizadas?: string[];
  /**
   * Cor fixa de uma série ou categoria. Chaveado pelo nome, e não pelo índice,
   * para a escolha sobreviver a uma consulta que devolva as colunas em outra
   * ordem.
   */
  porNome?: Record<string, string>;
};

export type DashboardWidgetDef = {
  id: string;
  tipo: string;
  titulo: string;
  layout: DashboardWidgetLayout;
  filtros?: string[];
  formato?: DashboardWidgetFormat;
  colunas?: DashboardColumnDef[];
  ordenacao?: DashboardSortDef;
  pivot?: DashboardPivotDef;
  grafico?: DashboardChartDef;
  gauge?: DashboardGaugeDef;
  aba?: string;
  texto?: string;
  cores?: DashboardWidgetColorsDef;
};

type DashboardTabDef = {
  id: string;
  titulo: string;
};

export type DashboardModel = {
  schemaVersion: number;
  id: string;
  titulo: string;
  modulo?: string;
  descricao?: string;
  grade: {
    colunas: number;
    alturaLinha: number;
  };
  filtros: DashboardFilterDef[];
  widgets: DashboardWidgetDef[];
  abas?: DashboardTabDef[];
};

export type DashboardModelResult = {
  convertido: boolean;
  conversaoDesatualizada?: boolean;
  atualizacaoPendente?: boolean;
  modelo?: DashboardModel;
};

/** O período anterior do KPI: a janela comparada e o que ela valia. */
export type DashboardComparativoData = {
  inicio: string;
  fim: string;
  valorAnterior?: number;
  variacaoPercentual?: number;
};

export type KpiWidgetData = {
  tipo: 'kpi';
  valor: number;
  variacaoPercentual?: number;
  spark?: number[];
  comparativo?: DashboardComparativoData;
};

export type SeriesWidgetData = {
  tipo: 'series';
  categorias: string[];
  series: Array<{
    nome: string;
    valores: number[];
  }>;
};

export type FunnelWidgetData = {
  tipo: 'funil';
  etapas: Array<{
    nome: string;
    valor: number;
    taxa?: number | null;
  }>;
};

export type TableWidgetData = {
  tipo: 'tabela';
  colunas: string[];
  linhas: Array<Record<string, unknown>>;
};

export type PivotWidgetData = {
  tipo: 'pivot';
  eixoLinhas: string[];
  eixoColunas: string[];
  medidas: Array<{ titulo: string; formato?: DashboardColumnFormat }>;
  colunasPivot: string[][];
  linhasPivot: Array<{
    chave: string[];
    celulas: Array<Array<number | null>>;
  }>;
};

export type GaugeWidgetData = {
  tipo: 'gauge';
  medidores: Array<{
    titulo: string;
    valor: number;
    alvo?: number;
    formato?: DashboardColumnFormat;
  }>;
};

export type TextoWidgetData = {
  tipo: 'texto';
  /** A primeira linha da consulta, campo a campo, para preencher o texto. */
  valores: Record<string, unknown>;
};

export type DashboardWidgetData =
  | KpiWidgetData
  | SeriesWidgetData
  | FunnelWidgetData
  | TableWidgetData
  | PivotWidgetData
  | GaugeWidgetData
  | TextoWidgetData;

export type DashboardFilterValues = Record<string, string>;

/**
 * A consulta rodou e não devolveu nada — situação normal quando o filtro é
 * restritivo. Vale para o quadro dizer isso em vez de ficar em branco, o que
 * o usuário lê como tela quebrada. KPI fica de fora de propósito: zero é um
 * valor, não a ausência dele.
 */
/**
 * Campo citado no texto do cartão. `{Faturamento}` usa o valor como veio;
 * `{Faturamento:moeda}` formata. Campo que a consulta não devolve fica visível
 * como está escrito — some no meio da frase é pior do que aparecer errado.
 */
const CAMPO_NO_TEXTO = /\{([A-Za-z_][A-Za-z0-9_ ]*?)(?::(moeda|percentual|decimal|inteiro))?\}/g;

export function camposDoTexto(texto?: string): string[] {
  const campos: string[] = [];

  for (const casado of (texto ?? '').matchAll(CAMPO_NO_TEXTO)) {
    const campo = casado[1].trim();

    if (campo && !campos.includes(campo)) campos.push(campo);
  }

  return campos;
}

/** O cartão de texto só consulta o banco quando cita algum campo. */
export function textoDinamico(widget: DashboardWidgetDef): boolean {
  return widget.tipo === 'texto' && camposDoTexto(widget.texto).length > 0;
}

function formatarValorDoTexto(valor: unknown, formato?: string): string {
  if (valor === null || valor === undefined) return '';

  const numero = typeof valor === 'number' ? valor : Number(String(valor).replace(',', '.'));

  if (!formato || Number.isNaN(numero)) return String(valor);

  if (formato === 'moeda') {
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  if (formato === 'percentual') {
    return `${numero.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
  }

  if (formato === 'inteiro') {
    return numero.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  }

  return numero.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

/**
 * O nome do campo é o que o SQL Server devolve — o **alias**, quando a consulta
 * tem um (`c.[Nome] AS Consultor` devolve `Consultor`, não `Nome`). A busca
 * ignora a caixa porque quem escreve a frase digita o nome de memória, e o
 * servidor também trata a linha da consulta sem diferenciar maiúsculas.
 */
function valorDoCampo(
  valores: Record<string, unknown>,
  nome: string
): { achou: boolean; valor?: unknown } {
  if (nome in valores) return { achou: true, valor: valores[nome] };

  const chave = Object.keys(valores).find(
    (candidata) => candidata.toLowerCase() === nome.toLowerCase()
  );

  return chave === undefined ? { achou: false } : { achou: true, valor: valores[chave] };
}

/** Campos citados no texto que a consulta não devolve, na grafia escrita. */
export function camposAusentes(texto?: string, colunas?: string[]): string[] {
  if (!colunas?.length) return [];

  const disponiveis = colunas.map((coluna) => coluna.toLowerCase());

  return camposDoTexto(texto).filter(
    (campo) => !disponiveis.includes(campo.toLowerCase())
  );
}

export function resolverTexto(texto: string, valores?: Record<string, unknown>): string {
  return texto.replace(CAMPO_NO_TEXTO, (bruto, campo: string, formato?: string) => {
    if (!valores) return bruto;

    const encontrado = valorDoCampo(valores, campo.trim());

    if (!encontrado.achou) return bruto;

    return formatarValorDoTexto(encontrado.valor, formato);
  });
}

/**
 * Pizza e treemap repartem um todo. O caso comum é uma medida repartida entre as
 * categorias — faturamento por região. Mas a consulta de uma linha só com
 * várias medidas (`SELECT COUNT(*) AS Clientes, SUM(...) AS Veiculos`) não tem
 * categoria para repartir: aí a fatia é a própria medida, que é o que o autor
 * desenhou ao pôr duas medidas numa pizza.
 */
export function medidasSaoAsFatias(data: SeriesWidgetData): boolean {
  return data.series.length > 1 && data.categorias.length <= 1;
}

export function semDados(data: DashboardWidgetData): boolean {
  if (data.tipo === 'series') {
    return data.categorias.length === 0 || data.series.every((serie) => serie.valores.length === 0);
  }

  if (data.tipo === 'funil') return data.etapas.length === 0;

  if (data.tipo === 'tabela') return data.linhas.length === 0;

  if (data.tipo === 'pivot') return data.linhasPivot.length === 0;

  if (data.tipo === 'gauge') return data.medidores.length === 0;

  return false;
}

export const WIDGET_TYPES = {
  kpi: 'kpi',
  linha: 'grafico-linha',
  barras: 'grafico-barras',
  barrasHorizontal: 'grafico-barras-horizontal',
  funil: 'funil',
  pizza: 'grafico-pizza',
  tabela: 'tabela',
  pivot: 'pivot',
  texto: 'texto',
  treemap: 'treemap',
  gauge: 'gauge',
} as const;

export const SUPPORTED_WIDGET_TYPES: string[] = [
  WIDGET_TYPES.kpi,
  WIDGET_TYPES.linha,
  WIDGET_TYPES.barras,
  WIDGET_TYPES.barrasHorizontal,
  WIDGET_TYPES.funil,
  WIDGET_TYPES.pizza,
  WIDGET_TYPES.tabela,
  WIDGET_TYPES.pivot,
  WIDGET_TYPES.texto,
  WIDGET_TYPES.treemap,
  WIDGET_TYPES.gauge,
];
